import React, { useRef, useEffect, MutableRefObject } from 'react';
import { PitchResult } from '../utils/pitchDetection';
import { estimateFormants, guessVowel, VOWEL_TABLE } from '../utils/formants';

interface Props {
  timeDomainRef: MutableRefObject<Float32Array | null>;
  pitchRef: MutableRefObject<PitchResult | null>;
  sampleRate: number;
  /** Output for the tracker overlay: smoothed [F1, F2, F3] or null. */
  formantsOutRef?: MutableRefObject<number[] | null>;
}

// Vowel chart axes (log). F2 runs right-to-left, F1 top-to-bottom, the way
// phoneticians draw it: front vowels left, close vowels top.
const F2_MAX = 2800, F2_MIN = 700;
const F1_MIN = 250, F1_MAX = 1000;
const SMOOTH = 0.3;
const TRAIL = 24;
const ANALYSE_EVERY = 2; // frames

export default function FormantsPanel({ timeDomainRef, pitchRef, sampleRate, formantsOutRef }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const smoothRef = useRef<number[] | null>(null);
  const trailRef = useRef<{ f1: number; f2: number }[]>([]);
  const frameRef = useRef(0);
  const holdRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    let animId: number;

    const draw = () => {
      const dpr = window.devicePixelRatio || 1;
      const cw = Math.max(1, Math.round(canvas.clientWidth * dpr));
      const ch = Math.max(1, Math.round(canvas.clientHeight * dpr));
      if (canvas.width !== cw || canvas.height !== ch) { canvas.width = cw; canvas.height = ch; }
      const W = cw, H = ch;
      ctx.clearRect(0, 0, W, H);

      // ── Analyse (throttled) ──
      if ((frameRef.current++ % ANALYSE_EVERY) === 0) {
        const td = timeDomainRef.current;
        const p = pitchRef.current;
        const est = td && p && p.clarity > 0.5 ? estimateFormants(td, sampleRate) : null;
        if (est && est.formants.length >= 2) {
          const f = est.formants.slice(0, 3);
          const s = smoothRef.current;
          if (s && s.length === f.length) {
            for (let i = 0; i < f.length; i++) s[i] += (f[i] - s[i]) * SMOOTH;
          } else smoothRef.current = f;
          holdRef.current = 12;
          const cur = smoothRef.current!;
          trailRef.current.push({ f1: cur[0], f2: cur[1] });
          if (trailRef.current.length > TRAIL) trailRef.current.shift();
        } else if (holdRef.current > 0) {
          holdRef.current--;
        } else {
          smoothRef.current = null;
          if (trailRef.current.length) trailRef.current.shift();
        }
        if (formantsOutRef) formantsOutRef.current = holdRef.current > 0 ? smoothRef.current : null;
      }

      const padL = 8 * dpr, padR = 8 * dpr, padT = 18 * dpr, padB = 12 * dpr;
      const plotW = W - padL - padR, plotH = H - padT - padB;
      const xOf = (f2: number) => padL + (1 - (Math.log(f2 / F2_MIN) / Math.log(F2_MAX / F2_MIN))) * plotW;
      const yOf = (f1: number) => padT + (Math.log(f1 / F1_MIN) / Math.log(F1_MAX / F1_MIN)) * plotH;

      // Frame
      ctx.strokeStyle = 'rgba(69,124,159,0.5)';
      ctx.lineWidth = 1 * dpr;
      ctx.strokeRect(padL + 0.5, padT + 0.5, plotW - 1, plotH - 1);

      // Vowel landmarks
      ctx.font = `${10 * dpr}px "Georgia", serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      for (const v of VOWEL_TABLE) {
        const x = xOf(v.f2), y = yOf(v.f1);
        if (x < padL || x > W - padR || y < padT || y > H - padB) continue;
        ctx.fillStyle = 'rgba(122,109,140,0.75)';
        ctx.fillText(v.symbol, x, y);
      }

      // Trail + current point
      const trail = trailRef.current;
      for (let i = 0; i < trail.length; i++) {
        const a = (i + 1) / trail.length;
        const x = xOf(trail[i].f2), y = yOf(trail[i].f1);
        ctx.fillStyle = `rgba(209,125,195,${0.15 + 0.5 * a})`;
        ctx.beginPath(); ctx.arc(x, y, (1.5 + 2 * a) * dpr, 0, Math.PI * 2); ctx.fill();
      }
      const cur = holdRef.current > 0 ? smoothRef.current : null;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'alphabetic';
      if (cur && cur.length >= 2) {
        const x = xOf(cur[1]), y = yOf(cur[0]);
        ctx.strokeStyle = 'rgba(250,237,175,0.95)';
        ctx.lineWidth = 1.5 * dpr;
        ctx.beginPath(); ctx.arc(x, y, 5 * dpr, 0, Math.PI * 2); ctx.stroke();
        const g = guessVowel(cur[0], cur[1]);
        ctx.font = `bold ${12 * dpr}px "Courier New", monospace`;
        ctx.fillStyle = 'rgba(250,237,175,0.95)';
        const head = g.distance < 0.35 ? `${g.symbol}  as in ${g.keyword}` : 'between vowels';
        ctx.fillText(head, padL, 12 * dpr);
        ctx.font = `${9 * dpr}px "Courier New", monospace`;
        ctx.fillStyle = 'rgba(182,173,196,0.85)';
        const nums = cur.map((f, i) => `F${i + 1} ${Math.round(f)}`).join('  ');
        ctx.textAlign = 'right';
        ctx.fillText(nums, W - padR, 12 * dpr);
        ctx.textAlign = 'left';
      } else {
        ctx.font = `bold ${10 * dpr}px "Courier New", monospace`;
        ctx.fillStyle = 'rgba(122,109,140,0.85)';
        ctx.fillText('sing a vowel', padL, 12 * dpr);
      }

      // Axis hints
      ctx.font = `${8 * dpr}px "Courier New", monospace`;
      ctx.fillStyle = 'rgba(122,109,140,0.6)';
      ctx.fillText('front ←  F2  → back', padL + 2 * dpr, H - 3 * dpr);
      ctx.textAlign = 'right';
      ctx.fillText('F1 ↓ open', W - padR, H - 3 * dpr);
      ctx.textAlign = 'left';

      animId = requestAnimationFrame(draw);
    };

    animId = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animId);
  }, [timeDomainRef, pitchRef, sampleRate, formantsOutRef]);

  return (
    <div className="rail-canvas-wrap">
      <canvas ref={canvasRef} className="rail-canvas" />
    </div>
  );
}
